/**
 * The auth store holds on to all of the auth data
 */
import {derived, writable, get} from "svelte/store";
import {video_store} from "../Utils/stores";
import {apiBaseURL, supportsLogin} from "../services/configService";

const TRACKING_API_URL =  apiBaseURL();

function createAuthStore() {
  const store = writable({
    loggingIn: false,
    loggedIn: false,
    errorLogin: false,
    user: {
      email: null,
      customerId: null,
    },
  });

  function loggingIn() {
    store.update((u) => {
      u.loggingIn = true;
      return u;
    });
  }

  function finishLoggingIn() {
    store.update((u) => {
      u.loggingIn = false;
      return u;
    });
  }

  /**
   * This function calls the auth service to see if
   * we are logged in. In the event we are it logs us in
   */
  async function init() {
    if (!supportsLogin()) {
      setLoggedOut();
      return;
    }
      store.update((u) => {
      u.loggingIn = true;
      return u;
    });

    try {
      const response = await fetch(`${TRACKING_API_URL}/user` , {
        credentials: "include"
      });
      if (!response.ok) {
        finishLoggingIn();
        return;
      }

      const userDetails = await response.json();
      setLoggedIn({email: userDetails.userName, customerId: userDetails.customerId})
    } catch (e) {
      setLoggedOut();
      return;
    }
  }

  async function login(username, password) {
    if (!supportsLogin()) {
      return;
    }
    store.update((u) => {
      u.loggingIn = true;
      return u;
    });

    const url = new URL(`${TRACKING_API_URL}/login`);
    url.searchParams.set("username", username);
    url.searchParams.set("password", password);

    try {
      const response = await fetch(url, {
        method: "POST",
        credentials: "include",
      });
      if (!response.ok) {
        setLoggedOut();
        return;
      }

      const userDetails = await response.json();
      setLoggedIn({email: userDetails.userName, customerId: userDetails.customerId });
      finishLoggingIn();
    } catch (e) {
      setLoggedOut();
      sendError();
      return;
    }
  }

  /**
   * Log the user in
   * @param{{email: string, customerId: string}} userDetails
   */
  function setLoggedIn(userDetails) {
    store.update((u) => {
      u.loggedIn = true;
      u.errorLogin = false;
      u.user.email = userDetails.email;
      u.user.customerId = userDetails.customerId;
      return u;
    });
  }

  /**
   * Log the user out
   */
  function setLoggedOut() {
    store.update((u) => {
      u.loggingIn = false;
      u.loggedIn = false;
      u.errorLogin = false;
      u.user.email = null;
      u.user.customerId = null;
      return u;
    });
  }

  function sendError() {
    store.update((u) => {
      u.errorLogin = true;
      return u;
    });
  }

  return {
    subscribe: store.subscribe,
    update: store.update,
    set: store.set,
    init,
    login,
  };
}

export const authStore = createAuthStore();
authStore.init();

function createProgressStore() {
  const store = writable({
    watched: {},
    recentlyWatched: [],
    last: null
  });

  /**
   * Initialize the progress store
   */
  const init = async () => {
    try {
      const response = await fetch(`${TRACKING_API_URL}/watched`, {
        credentials: "include",
      });
      if (!response.ok) {
        return;
      }

      const videos = await response.json();
      store.update((u) => {
        u.watched = videos.watched
        u.last = videos.last
        return u;
      });
    } catch (e) {
      console.error(e);
      return;
    }
  };

  /**
   * Mark the given video as watched
   * @param{string} video slug
   */
  const markWatched = async (video) => {
    if (!supportsLogin()) {
      return;
    }
    const url = new URL(`${TRACKING_API_URL}/video`);
    url.searchParams.set("video", video);

    try {
      const response = await fetch(url, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        store.update(u => {
          u.watched[video] = true
          return u;
        })
      }

    } catch (e) {
      console.error(e)
    }
  };

  return {
    subscribe: store.subscribe,
    update: store.update,
    set: store.set,
    init,
    markWatched,
  };
}
export const progressStore = createProgressStore();

/**
 * Have the progress store subscribe to the auth stroe
 * When we log in then populate the store
 * When we log out then clear the store
 */
authStore.subscribe(async ($store) => {
  if ($store.loggedIn) {
    await progressStore.init();
  }
});

/**
 *  Calculates the progress of the videos in the videoList vs. the watched map
 */
const calculateProgress = (videoList, watchedMap) => {
  const progress =  {
    total: videoList.length,
    watched: 0

  }
  videoList.map(video => {
    if (watchedMap[video.slug.current]) {
      progress.watched += 1
    }
  })
  return progress;
}

export const playlistProgressStore = derived(
  [video_store, progressStore],
  ([$videoStore, $progressStore]) => {
    const result = {
      // Map : category -> {total, watched}
      categoryProgress: {},
      playlistProgress: {}
    }

    $videoStore.categories.map(cat => {
      result.categoryProgress[cat.slug.current] = calculateProgress(cat.videos, $progressStore.watched)
    } )

    $videoStore.playlists.map(playlist => {
      result.playlistProgress[playlist.slug.current] = calculateProgress(playlist.videos, $progressStore.watched)
    } )

    return result;
  }
)


/**
 *  Post video watched on soft end or at end
 */

export async function postWatchedVideo(videoslug, category, playlist, project) {
  const watched = get(progressStore).watched;
  if (watched && watched[videoslug] && watched[videoslug].watched) {
    return;
  }
  const url = new URL(`${TRACKING_API_URL}/markWatched`);
  url.searchParams.set("video",videoslug);
  url.searchParams.set("category",category);
  url.searchParams.set("playlist",playlist);
  url.searchParams.set("project",project);

  fetch(url, {
    method: "POST",
    credentials: "include",
    mode: "cors"
  }).then(async res => {
    const videos = await res.json();
    progressStore.update(u => {
      u.watched = videos.watched
      u.last = videos.last
      return u;
      });
  });
}
